/**
 * Route registry – the single list of ALL HTTP endpoints with their request/response schemas.
 *
 * - WS2 builds the Hono/OpenAPI routes from it (`@hono/zod-openapi`, path syntax `:param`).
 * - The web API client (`apps/web/src/api/client.ts`) is typed from it.
 * - A unit test asserts uniqueness and that every route has schemas.
 *
 * `auth`:
 *   public               – no authentication (rate limits/Turnstile still apply)
 *   registration-session – `__Host-lsc_reg` cookie required (§16)
 *   admin                – `__Host-lsc_admin` cookie required (ADR 014)
 *   hmac-signature       – n8n HMAC (`X-LSC-Timestamp`, `X-LSC-Signature`)
 *   svix-signature       – Resend webhook (Svix)
 * `csrf`: mutating browser routes must pass CSRF checks (admin: `X-CSRF-Token`; public: Origin +
 * `X-LSC-Client`, details in WS2 ADR).
 */
import type { z } from 'zod';
import { ErrorCode } from '../errors';
import {
  AdminLoginRequestSchema,
  AdminLoginResponseSchema,
  AdminMeResponseSchema,
  AdminUserListResponseSchema,
  AdminUserSchema,
  AdminVerifyRequestSchema,
  AdminVerifyResponseSchema,
  CreateAdminUserRequestSchema,
  UpdateAdminUserRequestSchema,
} from './admin-auth';
import {
  AdminResendVerificationResponseSchema,
  AuditEventListQuerySchema,
  AuditEventListResponseSchema,
  BatchCsvQuerySchema,
  BatchDetailSchema,
  BatchListQuerySchema,
  BatchListResponseSchema,
  CodeListQuerySchema,
  CodeListResponseSchema,
  CodeResponseSchema,
  CompanyDetailSchema,
  CompanyListQuerySchema,
  CompanyListResponseSchema,
  CreateBatchRequestSchema,
  CreateCompanyRequestSchema,
  DashboardResponseSchema,
  DisableBatchRequestSchema,
  DisableBatchResponseSchema,
  DisableCodeRequestSchema,
  DocumentResponseSchema,
  FindingResponseSchema,
  JobListQuerySchema,
  JobListResponseSchema,
  JobResponseSchema,
  OperationsResponseSchema,
  RegistrationDetailResponseSchema,
  RegistrationListQuerySchema,
  RegistrationListResponseSchema,
  ReplaceCodeRequestSchema,
  ReplaceCodeResponseSchema,
  RequestBatchDocumentRequestSchema,
  ResolveFindingRequestSchema,
  RetryProvisioningResponseSchema,
  RevealCodeResponseSchema,
  SheetSyncResponseSchema,
  UpdateCompanyRequestSchema,
} from './admin';
import { EmptyBodySchema, IdParamsSchema } from './common';
import {
  ResendWebhookPayloadSchema,
  SheetResultRequestSchema,
  SheetResultResponseSchema,
  WebhookAckResponseSchema,
} from './internal';
import {
  ActivationCheckResponseSchema,
  ActivationCodeParamsSchema,
  ChangeEmailRequestSchema,
  ChangeEmailResponseSchema,
  HealthResponseSchema,
  PublicConfigResponseSchema,
  ReadyResponseSchema,
  RegistrationStatusResponseSchema,
  ResendVerificationRequestSchema,
  ResendVerificationResponseSchema,
  StartRegistrationRequestSchema,
  StartRegistrationResponseSchema,
  VerifyEmailRequestSchema,
  VerifyEmailResponseSchema,
} from './public';

export type HttpMethod = 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
export type RouteAuth =
  'public' | 'registration-session' | 'admin' | 'hmac-signature' | 'svix-signature';
export type ResponseContentType =
  'application/json' | 'text/csv' | 'application/pdf' | 'application/zip' | 'none';

export interface RouteContract {
  readonly method: HttpMethod;
  /** Full path (Hono syntax). */
  readonly path: string;
  readonly summary: string;
  readonly auth: RouteAuth;
  readonly csrf: boolean;
  /** true = always required, 'conditional' = required after N failed attempts (code check). */
  readonly turnstile: boolean | 'conditional';
  readonly rateLimited: boolean;
  readonly params?: z.ZodType;
  readonly query?: z.ZodType;
  readonly body?: z.ZodType;
  readonly successStatus: 200 | 201 | 202 | 204;
  /** JSON response schema; null for 204 and binary/CSV responses. */
  readonly response: z.ZodType | null;
  readonly responseContentType: ResponseContentType;
  /** Domain errors this route can return in addition to VALIDATION_FAILED/INTERNAL_ERROR. */
  readonly errors: readonly ErrorCode[];
}

const ADMIN_ERRORS = [
  ErrorCode.UNAUTHORIZED,
  ErrorCode.SESSION_EXPIRED,
  ErrorCode.FORBIDDEN,
] as const;
const ADMIN_MUTATION_ERRORS = [...ADMIN_ERRORS, ErrorCode.CSRF_INVALID] as const;
const SESSION_ERRORS = [ErrorCode.REGISTRATION_SESSION_INVALID, ErrorCode.RATE_LIMITED] as const;
const SIGNATURE_ERRORS = [
  ErrorCode.SIGNATURE_INVALID,
  ErrorCode.TIMESTAMP_EXPIRED,
  ErrorCode.WEBHOOK_REPLAYED,
] as const;

export const API_ROUTES = {
  // ---------------------------------------------------------------- health (outside /api/v1)
  health: {
    method: 'GET',
    path: '/health',
    summary: 'Liveness: the worker runs.',
    auth: 'public',
    csrf: false,
    turnstile: false,
    rateLimited: false,
    successStatus: 200,
    response: HealthResponseSchema,
    responseContentType: 'application/json',
    errors: [],
  },
  ready: {
    method: 'GET',
    path: '/ready',
    summary: 'Readiness: D1 reachable (`SELECT 1`). 503 with the same body when not ready.',
    auth: 'public',
    csrf: false,
    turnstile: false,
    rateLimited: false,
    successStatus: 200,
    response: ReadyResponseSchema,
    responseContentType: 'application/json',
    errors: [],
  },

  // ---------------------------------------------------------------- public (§56)
  publicConfig: {
    method: 'GET',
    path: '/api/v1/public/config',
    summary: 'Runtime configuration for the SPA (URLs, Turnstile site key).',
    auth: 'public',
    csrf: false,
    turnstile: false,
    rateLimited: false,
    successStatus: 200,
    response: PublicConfigResponseSchema,
    responseContentType: 'application/json',
    errors: [],
  },
  checkActivationCode: {
    method: 'GET',
    path: '/api/v1/public/activation/:code',
    summary: 'Check an activation code (§14). Turnstile via X-Turnstile-Token after N failures.',
    auth: 'public',
    csrf: false,
    turnstile: 'conditional',
    rateLimited: true,
    params: ActivationCodeParamsSchema,
    successStatus: 200,
    response: ActivationCheckResponseSchema,
    responseContentType: 'application/json',
    errors: [
      ErrorCode.ACTIVATION_CODE_INVALID,
      ErrorCode.ACTIVATION_CODE_USED,
      ErrorCode.ACTIVATION_CODE_DISABLED,
      ErrorCode.ACTIVATION_CODE_EXPIRED,
      ErrorCode.BATCH_DISABLED,
      ErrorCode.ACTIVATION_ALREADY_PENDING,
      ErrorCode.TURNSTILE_REQUIRED,
      ErrorCode.TURNSTILE_FAILED,
      ErrorCode.RATE_LIMITED,
    ],
  },
  startRegistration: {
    method: 'POST',
    path: '/api/v1/public/activation/:code/start',
    summary:
      'Start the registration (§15): code → PENDING_EMAIL, session cookie, verification email.',
    auth: 'public',
    csrf: true,
    turnstile: true,
    rateLimited: true,
    params: ActivationCodeParamsSchema,
    body: StartRegistrationRequestSchema,
    successStatus: 201,
    response: StartRegistrationResponseSchema,
    responseContentType: 'application/json',
    errors: [
      ErrorCode.ACTIVATION_CODE_INVALID,
      ErrorCode.ACTIVATION_CODE_USED,
      ErrorCode.ACTIVATION_CODE_DISABLED,
      ErrorCode.ACTIVATION_CODE_EXPIRED,
      ErrorCode.BATCH_DISABLED,
      ErrorCode.ACTIVATION_ALREADY_PENDING,
      ErrorCode.TURNSTILE_FAILED,
      ErrorCode.CSRF_INVALID,
      ErrorCode.RATE_LIMITED,
    ],
  },
  resendVerification: {
    method: 'POST',
    path: '/api/v1/public/registration/resend-verification',
    summary: 'Resend the verification email (§20), cooldown 60 s.',
    auth: 'registration-session',
    csrf: true,
    turnstile: true,
    rateLimited: true,
    body: ResendVerificationRequestSchema,
    successStatus: 202,
    response: ResendVerificationResponseSchema,
    responseContentType: 'application/json',
    errors: [
      ...SESSION_ERRORS,
      ErrorCode.RESEND_TOO_EARLY,
      ErrorCode.RESEND_LIMIT_REACHED,
      ErrorCode.INVALID_STATE_TRANSITION,
      ErrorCode.TURNSTILE_FAILED,
      ErrorCode.CSRF_INVALID,
    ],
  },
  changeEmail: {
    method: 'PATCH',
    path: '/api/v1/public/registration/email',
    summary: 'Correct the email while PENDING_EMAIL (§19); old tokens invalidated, expiry reset.',
    auth: 'registration-session',
    csrf: true,
    turnstile: true,
    rateLimited: true,
    body: ChangeEmailRequestSchema,
    successStatus: 200,
    response: ChangeEmailResponseSchema,
    responseContentType: 'application/json',
    errors: [
      ...SESSION_ERRORS,
      ErrorCode.RESEND_LIMIT_REACHED,
      ErrorCode.INVALID_STATE_TRANSITION,
      ErrorCode.TURNSTILE_FAILED,
      ErrorCode.CSRF_INVALID,
    ],
  },
  verifyEmail: {
    method: 'POST',
    path: '/api/v1/public/verify',
    summary: 'Redeem a verification token (§17, §21); sets the registration session cookie.',
    auth: 'public',
    csrf: true,
    turnstile: false,
    rateLimited: true,
    body: VerifyEmailRequestSchema,
    successStatus: 200,
    response: VerifyEmailResponseSchema,
    responseContentType: 'application/json',
    errors: [
      ErrorCode.VERIFICATION_TOKEN_INVALID,
      ErrorCode.VERIFICATION_TOKEN_EXPIRED,
      ErrorCode.VERIFICATION_TOKEN_USED,
      ErrorCode.CSRF_INVALID,
      ErrorCode.RATE_LIMITED,
    ],
  },
  registrationStatus: {
    method: 'GET',
    path: '/api/v1/public/registration/status',
    summary: 'Status of the registration bound to the session (§52, polled).',
    auth: 'registration-session',
    csrf: false,
    turnstile: false,
    rateLimited: true,
    successStatus: 200,
    response: RegistrationStatusResponseSchema,
    responseContentType: 'application/json',
    errors: [...SESSION_ERRORS],
  },

  // ---------------------------------------------------------------- admin auth (ADR 014)
  adminLogin: {
    method: 'POST',
    path: '/api/v1/admin/auth/login',
    summary: 'Request a magic link. Always 202, never reveals whether the admin exists.',
    auth: 'public',
    csrf: true,
    turnstile: true,
    rateLimited: true,
    body: AdminLoginRequestSchema,
    successStatus: 202,
    response: AdminLoginResponseSchema,
    responseContentType: 'application/json',
    errors: [ErrorCode.TURNSTILE_FAILED, ErrorCode.RATE_LIMITED, ErrorCode.CSRF_INVALID],
  },
  adminVerify: {
    method: 'POST',
    path: '/api/v1/admin/auth/verify',
    summary: 'Redeem a magic link token → session cookie + CSRF token.',
    auth: 'public',
    csrf: true,
    turnstile: false,
    rateLimited: true,
    body: AdminVerifyRequestSchema,
    successStatus: 200,
    response: AdminVerifyResponseSchema,
    responseContentType: 'application/json',
    errors: [
      ErrorCode.LOGIN_LINK_INVALID,
      ErrorCode.LOGIN_LINK_EXPIRED,
      ErrorCode.FORBIDDEN,
      ErrorCode.RATE_LIMITED,
      ErrorCode.CSRF_INVALID,
    ],
  },
  adminLogout: {
    method: 'POST',
    path: '/api/v1/admin/auth/logout',
    summary: 'Revoke the current session server-side and clear cookies.',
    auth: 'admin',
    csrf: true,
    turnstile: false,
    rateLimited: false,
    body: EmptyBodySchema,
    successStatus: 204,
    response: null,
    responseContentType: 'none',
    errors: [...ADMIN_MUTATION_ERRORS],
  },
  adminMe: {
    method: 'GET',
    path: '/api/v1/admin/auth/me',
    summary: 'Current admin identity and session expiry.',
    auth: 'admin',
    csrf: false,
    turnstile: false,
    rateLimited: false,
    successStatus: 200,
    response: AdminMeResponseSchema,
    responseContentType: 'application/json',
    errors: [...ADMIN_ERRORS],
  },

  // ---------------------------------------------------------------- admin users
  listAdminUsers: {
    method: 'GET',
    path: '/api/v1/admin/admin-users',
    summary: 'List admin users.',
    auth: 'admin',
    csrf: false,
    turnstile: false,
    rateLimited: false,
    successStatus: 200,
    response: AdminUserListResponseSchema,
    responseContentType: 'application/json',
    errors: [...ADMIN_ERRORS],
  },
  createAdminUser: {
    method: 'POST',
    path: '/api/v1/admin/admin-users',
    summary: 'Add an admin (logs in via magic link).',
    auth: 'admin',
    csrf: true,
    turnstile: false,
    rateLimited: false,
    body: CreateAdminUserRequestSchema,
    successStatus: 201,
    response: AdminUserSchema,
    responseContentType: 'application/json',
    errors: [...ADMIN_MUTATION_ERRORS, ErrorCode.CONFLICT],
  },
  updateAdminUser: {
    method: 'PATCH',
    path: '/api/v1/admin/admin-users/:id',
    summary: 'Rename or (de)activate an admin; deactivation revokes all sessions.',
    auth: 'admin',
    csrf: true,
    turnstile: false,
    rateLimited: false,
    params: IdParamsSchema,
    body: UpdateAdminUserRequestSchema,
    successStatus: 200,
    response: AdminUserSchema,
    responseContentType: 'application/json',
    errors: [...ADMIN_MUTATION_ERRORS, ErrorCode.NOT_FOUND, ErrorCode.LAST_ACTIVE_ADMIN],
  },

  // ---------------------------------------------------------------- dashboard / operations
  dashboard: {
    method: 'GET',
    path: '/api/v1/admin/dashboard',
    summary: 'KPIs, recent activity and provisioning failures (§34).',
    auth: 'admin',
    csrf: false,
    turnstile: false,
    rateLimited: false,
    successStatus: 200,
    response: DashboardResponseSchema,
    responseContentType: 'application/json',
    errors: [...ADMIN_ERRORS],
  },
  operations: {
    method: 'GET',
    path: '/api/v1/admin/operations',
    summary: 'Failures, stuck jobs, DLQ, failed documents, bounces, findings (§109, §135).',
    auth: 'admin',
    csrf: false,
    turnstile: false,
    rateLimited: false,
    successStatus: 200,
    response: OperationsResponseSchema,
    responseContentType: 'application/json',
    errors: [...ADMIN_ERRORS],
  },
  resolveFinding: {
    method: 'POST',
    path: '/api/v1/admin/reconciliation-findings/:id/resolve',
    summary: 'Mark a reconciliation finding as resolved.',
    auth: 'admin',
    csrf: true,
    turnstile: false,
    rateLimited: false,
    params: IdParamsSchema,
    body: ResolveFindingRequestSchema,
    successStatus: 200,
    response: FindingResponseSchema,
    responseContentType: 'application/json',
    errors: [...ADMIN_MUTATION_ERRORS, ErrorCode.NOT_FOUND, ErrorCode.INVALID_STATE_TRANSITION],
  },
  listAuditEvents: {
    method: 'GET',
    path: '/api/v1/admin/audit-events',
    summary: 'Filterable audit log (§64).',
    auth: 'admin',
    csrf: false,
    turnstile: false,
    rateLimited: false,
    query: AuditEventListQuerySchema,
    successStatus: 200,
    response: AuditEventListResponseSchema,
    responseContentType: 'application/json',
    errors: [...ADMIN_ERRORS],
  },
  listJobs: {
    method: 'GET',
    path: '/api/v1/admin/jobs',
    summary: 'List integration jobs.',
    auth: 'admin',
    csrf: false,
    turnstile: false,
    rateLimited: false,
    query: JobListQuerySchema,
    successStatus: 200,
    response: JobListResponseSchema,
    responseContentType: 'application/json',
    errors: [...ADMIN_ERRORS],
  },
  getJob: {
    method: 'GET',
    path: '/api/v1/admin/jobs/:id',
    summary: 'Integration job detail.',
    auth: 'admin',
    csrf: false,
    turnstile: false,
    rateLimited: false,
    params: IdParamsSchema,
    successStatus: 200,
    response: JobResponseSchema,
    responseContentType: 'application/json',
    errors: [...ADMIN_ERRORS, ErrorCode.NOT_FOUND],
  },
  retryJob: {
    method: 'POST',
    path: '/api/v1/admin/jobs/:id/retry',
    summary:
      'Manually re-queue a DEAD/FAILED_RETRYABLE job (§135). Provisioning: use registration retry.',
    auth: 'admin',
    csrf: true,
    turnstile: false,
    rateLimited: false,
    params: IdParamsSchema,
    body: EmptyBodySchema,
    successStatus: 202,
    response: JobResponseSchema,
    responseContentType: 'application/json',
    errors: [...ADMIN_MUTATION_ERRORS, ErrorCode.NOT_FOUND, ErrorCode.INVALID_STATE_TRANSITION],
  },

  // ---------------------------------------------------------------- companies (§35, §36)
  listCompanies: {
    method: 'GET',
    path: '/api/v1/admin/companies',
    summary: 'List companies with seat statistics.',
    auth: 'admin',
    csrf: false,
    turnstile: false,
    rateLimited: false,
    query: CompanyListQuerySchema,
    successStatus: 200,
    response: CompanyListResponseSchema,
    responseContentType: 'application/json',
    errors: [...ADMIN_ERRORS],
  },
  createCompany: {
    method: 'POST',
    path: '/api/v1/admin/companies',
    summary: 'Create a company.',
    auth: 'admin',
    csrf: true,
    turnstile: false,
    rateLimited: false,
    body: CreateCompanyRequestSchema,
    successStatus: 201,
    response: CompanyDetailSchema,
    responseContentType: 'application/json',
    errors: [
      ...ADMIN_MUTATION_ERRORS,
      ErrorCode.CONFLICT,
      ErrorCode.LEARNINGSUITE_GROUP_NOT_FOUND,
      ErrorCode.LEARNINGSUITE_UNAVAILABLE,
    ],
  },
  getCompany: {
    method: 'GET',
    path: '/api/v1/admin/companies/:id',
    summary: 'Company detail with seat statistics (batches via listBatches?companyId=).',
    auth: 'admin',
    csrf: false,
    turnstile: false,
    rateLimited: false,
    params: IdParamsSchema,
    successStatus: 200,
    response: CompanyDetailSchema,
    responseContentType: 'application/json',
    errors: [...ADMIN_ERRORS, ErrorCode.NOT_FOUND],
  },
  updateCompany: {
    method: 'PATCH',
    path: '/api/v1/admin/companies/:id',
    summary: 'Update or (un)archive a company. Group ID changes only affect new batches.',
    auth: 'admin',
    csrf: true,
    turnstile: false,
    rateLimited: false,
    params: IdParamsSchema,
    body: UpdateCompanyRequestSchema,
    successStatus: 200,
    response: CompanyDetailSchema,
    responseContentType: 'application/json',
    errors: [
      ...ADMIN_MUTATION_ERRORS,
      ErrorCode.NOT_FOUND,
      ErrorCode.CONFLICT,
      ErrorCode.LEARNINGSUITE_GROUP_NOT_FOUND,
      ErrorCode.LEARNINGSUITE_UNAVAILABLE,
    ],
  },

  // ---------------------------------------------------------------- batches (§37, §38, §99)
  listBatches: {
    method: 'GET',
    path: '/api/v1/admin/batches',
    summary: 'List batches (filter by company/status).',
    auth: 'admin',
    csrf: false,
    turnstile: false,
    rateLimited: false,
    query: BatchListQuerySchema,
    successStatus: 200,
    response: BatchListResponseSchema,
    responseContentType: 'application/json',
    errors: [...ADMIN_ERRORS],
  },
  createBatch: {
    method: 'POST',
    path: '/api/v1/admin/batches',
    summary: 'Create a batch (status GENERATING; codes generated asynchronously in chunks).',
    auth: 'admin',
    csrf: true,
    turnstile: false,
    rateLimited: false,
    body: CreateBatchRequestSchema,
    successStatus: 201,
    response: BatchDetailSchema,
    responseContentType: 'application/json',
    errors: [...ADMIN_MUTATION_ERRORS, ErrorCode.NOT_FOUND, ErrorCode.COMPANY_ARCHIVED],
  },
  getBatch: {
    method: 'GET',
    path: '/api/v1/admin/batches/:id',
    summary: 'Batch detail incl. generation progress and documents.',
    auth: 'admin',
    csrf: false,
    turnstile: false,
    rateLimited: false,
    params: IdParamsSchema,
    successStatus: 200,
    response: BatchDetailSchema,
    responseContentType: 'application/json',
    errors: [...ADMIN_ERRORS, ErrorCode.NOT_FOUND],
  },
  disableBatch: {
    method: 'POST',
    path: '/api/v1/admin/batches/:id/disable',
    summary:
      'Disable a batch (§99): AVAILABLE/PENDING_EMAIL codes → DISABLED; active seats untouched.',
    auth: 'admin',
    csrf: true,
    turnstile: false,
    rateLimited: false,
    params: IdParamsSchema,
    body: DisableBatchRequestSchema,
    successStatus: 200,
    response: DisableBatchResponseSchema,
    responseContentType: 'application/json',
    errors: [
      ...ADMIN_MUTATION_ERRORS,
      ErrorCode.NOT_FOUND,
      ErrorCode.BATCH_NOT_READY,
      ErrorCode.INVALID_STATE_TRANSITION,
    ],
  },
  listBatchCodes: {
    method: 'GET',
    path: '/api/v1/admin/batches/:id/codes',
    summary: 'Codes of a batch with registration summary (§39).',
    auth: 'admin',
    csrf: false,
    turnstile: false,
    rateLimited: false,
    params: IdParamsSchema,
    query: CodeListQuerySchema,
    successStatus: 200,
    response: CodeListResponseSchema,
    responseContentType: 'application/json',
    errors: [...ADMIN_ERRORS, ErrorCode.NOT_FOUND],
  },
  requestBatchDocument: {
    method: 'POST',
    path: '/api/v1/admin/batches/:id/documents',
    summary:
      'Request batch PDF/ZIP (async, Browser Run) or CSV (async, in parts). READY immediately on cache hit.',
    auth: 'admin',
    csrf: true,
    turnstile: false,
    rateLimited: false,
    params: IdParamsSchema,
    body: RequestBatchDocumentRequestSchema,
    successStatus: 202,
    response: DocumentResponseSchema,
    responseContentType: 'application/json',
    errors: [
      ...ADMIN_MUTATION_ERRORS,
      ErrorCode.NOT_FOUND,
      ErrorCode.BATCH_NOT_READY,
      ErrorCode.DOCUMENT_RENDER_UNAVAILABLE,
    ],
  },
  batchCsv: {
    method: 'GET',
    path: '/api/v1/admin/batches/:id/csv',
    summary:
      'CSV export (synchronous, UTF-8 BOM, `;`, no private emails by default). Only up to CSV_SYNC_MAX_CODES codes; larger batches → VALIDATION_FAILED, use BATCH_CSV document.',
    auth: 'admin',
    csrf: false,
    turnstile: false,
    rateLimited: false,
    params: IdParamsSchema,
    query: BatchCsvQuerySchema,
    successStatus: 200,
    response: null,
    responseContentType: 'text/csv',
    errors: [...ADMIN_ERRORS, ErrorCode.NOT_FOUND, ErrorCode.BATCH_NOT_READY],
  },
  batchSheetSync: {
    method: 'POST',
    path: '/api/v1/admin/batches/:id/sheet-sync',
    summary: 'Trigger Google Sheet sync via n8n (debounced).',
    auth: 'admin',
    csrf: true,
    turnstile: false,
    rateLimited: false,
    params: IdParamsSchema,
    body: EmptyBodySchema,
    successStatus: 202,
    response: SheetSyncResponseSchema,
    responseContentType: 'application/json',
    errors: [...ADMIN_MUTATION_ERRORS, ErrorCode.NOT_FOUND, ErrorCode.BATCH_NOT_READY],
  },

  // ---------------------------------------------------------------- documents
  getDocument: {
    method: 'GET',
    path: '/api/v1/admin/documents/:id',
    summary: 'Status of a generated document (poll until READY/FAILED).',
    auth: 'admin',
    csrf: false,
    turnstile: false,
    rateLimited: false,
    params: IdParamsSchema,
    successStatus: 200,
    response: DocumentResponseSchema,
    responseContentType: 'application/json',
    errors: [...ADMIN_ERRORS, ErrorCode.NOT_FOUND],
  },
  downloadDocument: {
    method: 'GET',
    path: '/api/v1/admin/documents/:id/download',
    summary:
      'Download a READY document from R2 (PDF/ZIP; BATCH_CSV as text/csv streamed from its parts). Content-Disposition without PII.',
    auth: 'admin',
    csrf: false,
    turnstile: false,
    rateLimited: false,
    params: IdParamsSchema,
    successStatus: 200,
    response: null,
    responseContentType: 'application/pdf',
    errors: [...ADMIN_ERRORS, ErrorCode.NOT_FOUND, ErrorCode.DOCUMENT_NOT_READY],
  },

  // ---------------------------------------------------------------- codes (§39, §100)
  disableCode: {
    method: 'POST',
    path: '/api/v1/admin/codes/:id/disable',
    summary: 'Disable an unredeemed code; a PENDING_EMAIL registration is cancelled.',
    auth: 'admin',
    csrf: true,
    turnstile: false,
    rateLimited: false,
    params: IdParamsSchema,
    body: DisableCodeRequestSchema,
    successStatus: 200,
    response: CodeResponseSchema,
    responseContentType: 'application/json',
    errors: [...ADMIN_MUTATION_ERRORS, ErrorCode.NOT_FOUND, ErrorCode.INVALID_STATE_TRANSITION],
  },
  enableCode: {
    method: 'POST',
    path: '/api/v1/admin/codes/:id/enable',
    summary: 'Re-enable a disabled, never-used, not replaced code.',
    auth: 'admin',
    csrf: true,
    turnstile: false,
    rateLimited: false,
    params: IdParamsSchema,
    body: EmptyBodySchema,
    successStatus: 200,
    response: CodeResponseSchema,
    responseContentType: 'application/json',
    errors: [
      ...ADMIN_MUTATION_ERRORS,
      ErrorCode.NOT_FOUND,
      ErrorCode.INVALID_STATE_TRANSITION,
      ErrorCode.BATCH_DISABLED,
    ],
  },
  replaceCode: {
    method: 'POST',
    path: '/api/v1/admin/codes/:id/replace',
    summary: 'Disable the code and create a replacement code (§100, audited).',
    auth: 'admin',
    csrf: true,
    turnstile: false,
    rateLimited: false,
    params: IdParamsSchema,
    body: ReplaceCodeRequestSchema,
    successStatus: 201,
    response: ReplaceCodeResponseSchema,
    responseContentType: 'application/json',
    errors: [
      ...ADMIN_MUTATION_ERRORS,
      ErrorCode.NOT_FOUND,
      ErrorCode.INVALID_STATE_TRANSITION,
      ErrorCode.BATCH_DISABLED,
    ],
  },
  revealCode: {
    method: 'POST',
    path: '/api/v1/admin/codes/:id/reveal',
    summary: 'Show the plaintext code (§39 „Code anzeigen“, audited as CODE_REVEALED).',
    auth: 'admin',
    csrf: true,
    turnstile: false,
    rateLimited: false,
    params: IdParamsSchema,
    body: EmptyBodySchema,
    successStatus: 200,
    response: RevealCodeResponseSchema,
    responseContentType: 'application/json',
    errors: [...ADMIN_MUTATION_ERRORS, ErrorCode.NOT_FOUND],
  },
  requestCodeDocument: {
    method: 'POST',
    path: '/api/v1/admin/codes/:id/documents',
    summary: 'Request a single-page PDF for one code (§39).',
    auth: 'admin',
    csrf: true,
    turnstile: false,
    rateLimited: false,
    params: IdParamsSchema,
    body: EmptyBodySchema,
    successStatus: 202,
    response: DocumentResponseSchema,
    responseContentType: 'application/json',
    errors: [
      ...ADMIN_MUTATION_ERRORS,
      ErrorCode.NOT_FOUND,
      ErrorCode.BATCH_NOT_READY,
      ErrorCode.DOCUMENT_RENDER_UNAVAILABLE,
    ],
  },

  // ---------------------------------------------------------------- registrations (§40, §98)
  listRegistrations: {
    method: 'GET',
    path: '/api/v1/admin/registrations',
    summary: 'List/filter registrations.',
    auth: 'admin',
    csrf: false,
    turnstile: false,
    rateLimited: false,
    query: RegistrationListQuerySchema,
    successStatus: 200,
    response: RegistrationListResponseSchema,
    responseContentType: 'application/json',
    errors: [...ADMIN_ERRORS],
  },
  getRegistration: {
    method: 'GET',
    path: '/api/v1/admin/registrations/:id',
    summary: 'Registration detail with jobs and audit timeline (§40).',
    auth: 'admin',
    csrf: false,
    turnstile: false,
    rateLimited: false,
    params: IdParamsSchema,
    successStatus: 200,
    response: RegistrationDetailResponseSchema,
    responseContentType: 'application/json',
    errors: [...ADMIN_ERRORS, ErrorCode.REGISTRATION_NOT_FOUND],
  },
  retryProvisioning: {
    method: 'POST',
    path: '/api/v1/admin/registrations/:id/retry',
    summary: 'Retry provisioning (§98): new job, verification unchanged, no new code.',
    auth: 'admin',
    csrf: true,
    turnstile: false,
    rateLimited: false,
    params: IdParamsSchema,
    body: EmptyBodySchema,
    successStatus: 202,
    response: RetryProvisioningResponseSchema,
    responseContentType: 'application/json',
    errors: [
      ...ADMIN_MUTATION_ERRORS,
      ErrorCode.REGISTRATION_NOT_FOUND,
      ErrorCode.INVALID_STATE_TRANSITION,
    ],
  },
  adminResendVerification: {
    method: 'POST',
    path: '/api/v1/admin/registrations/:id/resend-verification',
    summary: 'Resend the verification email on behalf of the participant (PENDING_EMAIL only).',
    auth: 'admin',
    csrf: true,
    turnstile: false,
    rateLimited: false,
    params: IdParamsSchema,
    body: EmptyBodySchema,
    successStatus: 202,
    response: AdminResendVerificationResponseSchema,
    responseContentType: 'application/json',
    errors: [
      ...ADMIN_MUTATION_ERRORS,
      ErrorCode.REGISTRATION_NOT_FOUND,
      ErrorCode.INVALID_STATE_TRANSITION,
      ErrorCode.RESEND_LIMIT_REACHED,
    ],
  },

  // ---------------------------------------------------------------- internal (§29)
  resendWebhook: {
    method: 'POST',
    path: '/api/v1/internal/resend/webhook',
    summary: 'Resend delivery events (delivered, bounced, complained), Svix-signed.',
    auth: 'svix-signature',
    csrf: false,
    turnstile: false,
    rateLimited: true,
    body: ResendWebhookPayloadSchema,
    successStatus: 200,
    response: WebhookAckResponseSchema,
    responseContentType: 'application/json',
    errors: [...SIGNATURE_ERRORS],
  },
  n8nSheetResult: {
    method: 'POST',
    path: '/api/v1/internal/n8n/sheet-result',
    summary: 'n8n reports the Google Sheet sync result (HMAC-signed).',
    auth: 'hmac-signature',
    csrf: false,
    turnstile: false,
    rateLimited: true,
    body: SheetResultRequestSchema,
    successStatus: 200,
    response: SheetResultResponseSchema,
    responseContentType: 'application/json',
    errors: [...SIGNATURE_ERRORS, ErrorCode.NOT_FOUND],
  },
} as const satisfies Record<string, RouteContract>;

export type ApiRoutes = typeof API_ROUTES;
export type RouteName = keyof ApiRoutes;
